/**
 * Crew chat's display state, shared by the panel, the launcher, the sidebar
 * and the notification code.
 *
 * One conversation, three sizes. The panel component stays mounted in every
 * size - minimising only hides it - which is what keeps the draft, pending
 * attachments, uploads in flight and the scroll position exactly as they
 * were. Nothing here sends, leaves, disconnects or marks anything read.
 *
 * The preferred width is remembered in this browser only, and always
 * constrained to the window when it is used.
 */

import { create } from 'zustand';

/** closed: put away with no launcher; reopened from the crew page. */
export type CrewChatMode = 'minimized' | 'closed' | 'open' | 'expanded';

const MODE_KEY = 'pogu.crewchat.mode';
const WIDTH_KEY = 'pogu.crewchat.width';
const EXPANDED_KEY = 'pogu.crewchat.expandedWidth';

export const CHAT_MIN = 360;
export const CHAT_OPEN_DEFAULT = 440;
export const CHAT_EXPANDED_DEFAULT = 760;

function read<T>(key: string, fallback: T, valid: (v: unknown) => boolean): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const v = JSON.parse(raw);
    return valid(v) ? (v as T) : fallback;
  } catch { return fallback; }
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage off */ }
}

type State = {
  mode: CrewChatMode;
  /** Preferred width of the regular panel, before the window constrains it. */
  width: number;
  /** Preferred width when expanded. */
  expandedWidth: number;
  /** Whether the newest message is on screen right now. Read by notifications. */
  atBottom: boolean;
  /** Unread messages per crew channel ('cc:<id>'), from the server's counts. */
  unread: Record<string, number>;
  /** Expanded far enough to take the whole content area; the crew page steps aside. */
  covering: boolean;

  setMode: (mode: CrewChatMode) => void;
  setCovering: (v: boolean) => void;
  setWidth: (w: number) => void;
  setExpandedWidth: (w: number) => void;
  setAtBottom: (v: boolean) => void;
  setUnread: (channelId: string, count: number) => void;
  seedUnread: (all: Record<string, number>) => void;
};

export const useCrewChat = create<State>((set, get) => ({
  mode: read<CrewChatMode>(MODE_KEY, 'open', (v) => v === 'minimized' || v === 'closed' || v === 'open' || v === 'expanded'),
  width: read<number>(WIDTH_KEY, CHAT_OPEN_DEFAULT, (v) => typeof v === 'number' && v >= CHAT_MIN && v <= 2400),
  expandedWidth: read<number>(EXPANDED_KEY, CHAT_EXPANDED_DEFAULT, (v) => typeof v === 'number' && v >= CHAT_MIN && v <= 3000),
  atBottom: true,
  unread: {},
  covering: false,

  setCovering(v) { if (get().covering !== v) set({ covering: v }); },
  setMode(mode) {
    if (get().mode === mode) return;
    write(MODE_KEY, mode);
    set({ mode });
  },
  setWidth(w) {
    const width = Math.round(Math.max(CHAT_MIN, w));
    write(WIDTH_KEY, width);
    set({ width });
  },
  setExpandedWidth(w) {
    const expandedWidth = Math.round(Math.max(CHAT_MIN, w));
    write(EXPANDED_KEY, expandedWidth);
    set({ expandedWidth });
  },
  setAtBottom(v) { if (get().atBottom !== v) set({ atBottom: v }); },
  setUnread(channelId, count) {
    if (!channelId.startsWith('cc:')) return;
    set({ unread: { ...get().unread, [channelId]: Math.max(0, count) } });
  },
  seedUnread(all) {
    const next: Record<string, number> = {};
    for (const [k, v] of Object.entries(all ?? {})) if (k.startsWith('cc:')) next[k] = v;
    set({ unread: next });
  }
}));

/** The width a panel may actually take in this window, keeping the page usable beside it. */
export function constrainWidth(preferred: number, viewport: number, reserve: number): number {
  return Math.max(CHAT_MIN, Math.min(preferred, viewport - reserve));
}
